import { Calendar, Clock, ExternalLink, Tag } from "lucide-react";
import React from "react";
import { blogPosts, getLatestPosts } from "../data/content";
import { Card, CardContent } from "./ui/card";

// Individual blog post card component
const BlogPostCard = ({ post, isDetailed = false }) => {
  return (
    <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm hover:-translate-y-1 transition-transform duration-300">
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <h3
              className={`font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 ${
                isDetailed ? "text-2xl" : "text-lg"
              }`}
            >
              {post.title}
            </h3>
            {post.featured && (
              <span className="bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                Featured
              </span>
            )}
          </div>

          {/* Meta information */}
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{new Date(post.date).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{post.readTime}</span>
            </div>
          </div>

          {/* Excerpt */}
          <p className="text-gray-700 leading-relaxed">{post.excerpt}</p>

          {/* Full content for detailed view */}
          {isDetailed && (
            <div className="prose prose-gray max-w-none">
              <div
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: post.content
                    .replace(/\n/g, "<br/>")
                    .replace(
                      /## /g,
                      '<h2 class="text-xl font-semibold mt-6 mb-3 text-gray-800">'
                    )
                    .replace(/<h2/g, "</p><h2")
                    .replace(/h2>/g, 'h2><p class="text-gray-700">'),
                }}
              />
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, index) => (
              <span
                key={index}
                className="flex items-center gap-1 bg-gradient-to-r from-blue-50 to-purple-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
          </div>

          {/* Read more link for preview cards */}
          {!isDetailed && (
            <div className="pt-2">
              <button className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1">
                Read more
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Main blog page component
const BlogPage = () => {
  const latestPosts = getLatestPosts(6);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="py-16 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">
            Thoughts & Writing
          </h1>
          <p className="text-lg text-gray-600">
            Insights on AI safety, infrastructure, and building for the future
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pb-20">
        {/* Featured Posts */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
            Featured Posts
          </h2>
          <div className="grid gap-6">
            {blogPosts
              .filter((post) => post.featured)
              .map((post) => (
                <BlogPostCard key={post.id} post={post} />
              ))}
          </div>
        </section>

        {/* All Posts */}
        <section>
          <h2 className="text-2xl font-semibold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
            All Posts
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {latestPosts.map((post) => (
              <BlogPostCard key={post.id} post={post} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

// Blog post detail component
const BlogPostDetail = ({ postId }) => {
  const post = blogPosts.find((p) => p.id === postId);

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Post Not Found
          </h1>
          <p className="text-gray-600">
            The blog post you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <BlogPostCard post={post} isDetailed={true} />
      </div>
    </div>
  );
};

export { BlogPage, BlogPostCard, BlogPostDetail };
export default BlogPage;
